import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Divider,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Checkbox,
    Stepper,
    Step,
    StepLabel,
    CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import axios from 'axios';
import { createPricelistCustomer } from '../Service/productServices';
import { API_BASE_URL } from 'helpers/urlHelper';
import * as XLSX from 'xlsx';

const AddCustomerPriceDrawer = ({ open, onClose, selectedPricelist, onPricelistAdded, products }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [productRows, setProductRows] = useState([]);
    const [insuranceCompanies, setInsuranceCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    
    const [globalConfig, setGlobalConfig] = useState({
        userid: '',
        minQuantity: 1,
        maxQuantity: '',
        startDate: '',
        endDate: '',
        currency: '€',
        priority: 0,
        active: true,
        couponCodes: ''
    });

    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // Cargar productos en filas
    useEffect(() => {
        if (open && products && products.length > 0) {
            const initialRows = products.map(product => ({
                productId: product._id,
                productCode: product.code,
                productName: product.name,
                originalPrice: product.price,
                customPrice: '',
                companyId: '',
                selected: false
            }));
            setProductRows(initialRows);
        }
    }, [open, products]);

    // Cargar companies desde la API
    useEffect(() => {
        const API_COMPANIES = `${API_BASE_URL}/companies?companyType=Aseguradora`; 
        const fetchCompanies = async () => {
            if (!open) return;
            
            setLoadingCompanies(true);
            try {
                const response = await axios.get(API_COMPANIES);
                console.log('📦 Companies response:', response.data);
                
                let companies = [];
                if (Array.isArray(response.data)) {
                    companies = response.data;
                } else if (response.data && Array.isArray(response.data.data)) {
                    companies = response.data.data;
                } else if (response.data && Array.isArray(response.data.companies)) {
                    companies = response.data.companies;
                }
                
                console.log('✅ Companies loaded:', companies);
                
                const insurers = companies.filter(c => 
                    c.companyType === 'insurance' || 
                    c.companyType === 'insurer' ||
                    c.companymedicalextension?.insurance?.length > 0
                );
                
                setInsuranceCompanies(insurers.length > 0 ? insurers : companies);
                
            } catch (error) {
                console.error('❌ Error loading companies:', error);
                setErrorMessage('Error loading insurance companies');
                setInsuranceCompanies([]);
            } finally {
                setLoadingCompanies(false);
            }
        };
        
        fetchCompanies();
    }, [open]);

    // 📥 EXPORTAR PLANTILLA EXCEL EN BLANCO
    const exportBlankTemplate = () => {
        const selectedRows = productRows.filter(row => row.selected);
        
        if (selectedRows.length === 0) {
            setErrorMessage('Please select at least one product to export');
            return;
        }

        // Crear estructura con productos seleccionados
        const templateData = selectedRows.map(row => ({
            'Product Code *': row.productCode,
            'Product Name': row.productName,
            'Original Price': row.originalPrice,
            'Custom Price *': '', // VACÍO para rellenar
            'Insurance Company ID *': '', // VACÍO para rellenar
            'Insurance Company Name': '(See Companies sheet)', // Referencia
            'Min Quantity': 1,
            'Max Quantity': '',
            'Priority': 0,
            'Currency': '€',
            'Active': 'Yes',
            'Start Date (YYYY-MM-DD)': '',
            'End Date (YYYY-MM-DD)': '',
            'Coupon Codes (comma separated)': ''
        }));

        // Crear hoja con lista de compañías disponibles
        const companiesData = insuranceCompanies.map(c => ({
            'Company ID': c._id,
            'Company Name': c.name
        }));

        // Crear workbook con 2 hojas
        const wb = XLSX.utils.book_new();
        
        // Hoja 1: Plantilla para rellenar
        const ws1 = XLSX.utils.json_to_sheet(templateData);
        XLSX.utils.book_append_sheet(wb, ws1, 'Products to Price');
        
        // Hoja 2: Lista de compañías (referencia)
        const ws2 = XLSX.utils.json_to_sheet(companiesData);
        XLSX.utils.book_append_sheet(wb, ws2, 'Insurance Companies');
        
        // Descargar
        XLSX.writeFile(wb, `pricelist_template_${Date.now()}.xlsx`);
        
        setSuccessMessage(`✅ Template downloaded with ${selectedRows.length} products!`);
        setTimeout(() => setSuccessMessage(''), 3000);
    };

// 📤 IMPORTAR DESDE EXCEL - VERSIÓN CORREGIDA
const importFromExcel = async (file) => {
    setLoading(true);
    setErrorMessage('');
    
    try {
        const reader = new FileReader();
        
        reader.onload = async (e) => {
            const data = new Uint8Array(e.target.result);
            // 🔥 CRÍTICO: cellDates: true para que XLSX convierta fechas automáticamente
            const workbook = XLSX.read(data, { 
                type: 'array', 
                cellDates: true,
                cellNF: false,
                cellText: false
            });
            
            // Leer primera hoja
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            
            // 🔥 raw: false para obtener valores formateados
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
                raw: false,
                dateNF: 'yyyy-mm-dd' // Formato de fecha deseado
            });
            
            console.log('📥 Imported data:', jsonData);
            
            // 🔥 FUNCIÓN MEJORADA PARA PARSEAR FECHAS
            const parseExcelDate = (dateValue) => {
                if (!dateValue || dateValue === '') return undefined;
                
                console.log('🔍 Parsing date:', dateValue, 'Type:', typeof dateValue);
                
                // Si ya es un objeto Date válido
                if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
                    const result = dateValue.toISOString().split('T')[0];
                    console.log('✅ Date object converted:', result);
                    return result;
                }
                
                // Si es string
                if (typeof dateValue === 'string') {
                    // Limpiar espacios
                    dateValue = dateValue.trim();
                    
                    // Ya está en formato YYYY-MM-DD
                    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                        console.log('✅ Already in YYYY-MM-DD format:', dateValue);
                        return dateValue;
                    }
                    
                    // Formato DD/MM/YYYY (español)
                    const ddmmyyyyMatch = dateValue.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
                    if (ddmmyyyyMatch) {
                        const day = ddmmyyyyMatch[1].padStart(2, '0');
                        const month = ddmmyyyyMatch[2].padStart(2, '0');
                        const year = ddmmyyyyMatch[3];
                        const result = `${year}-${month}-${day}`;
                        console.log('✅ DD/MM/YYYY converted:', result);
                        return result;
                    }
                    
                    // Formato MM/DD/YYYY (inglés)
                    const mmddyyyyMatch = dateValue.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
                    if (mmddyyyyMatch) {
                        const month = mmddyyyyMatch[1].padStart(2, '0');
                        const day = mmddyyyyMatch[2].padStart(2, '0');
                        const year = mmddyyyyMatch[3];
                        const result = `${year}-${month}-${day}`;
                        console.log('✅ MM/DD/YYYY converted:', result);
                        return result;
                    }
                    
                    // Intentar parsear con Date
                    const parsed = new Date(dateValue);
                    if (!isNaN(parsed.getTime())) {
                        const result = parsed.toISOString().split('T')[0];
                        console.log('✅ String parsed as Date:', result);
                        return result;
                    }
                }
                
                // Si es número (serial date de Excel: días desde 1900-01-01)
                if (typeof dateValue === 'number') {
                    const excelEpoch = new Date(1899, 11, 30); // 30 de diciembre de 1899
                    const date = new Date(excelEpoch.getTime() + dateValue * 86400000);
                    const result = date.toISOString().split('T')[0];
                    console.log('✅ Excel serial number converted:', result);
                    return result;
                }
                
                console.warn('⚠️ Could not parse date:', dateValue);
                return undefined;
            };
            
            // Validar y transformar datos
            const validRows = [];
            const errors = [];
            
            jsonData.forEach((row, index) => {
                console.log(`\n🔍 Processing row ${index + 2}:`, row);
                
                // Validaciones básicas
                if (!row['Product Code *']) {
                    errors.push(`Row ${index + 2}: Missing Product Code`);
                    return;
                }
                
                const customPrice = parseFloat(row['Custom Price *']);
                if (!customPrice || customPrice <= 0 || isNaN(customPrice)) {
                    errors.push(`Row ${index + 2}: Invalid Custom Price (${row['Custom Price *']})`);
                    return;
                }
                
                if (!row['Insurance Company ID *']) {
                    errors.push(`Row ${index + 2}: Missing Insurance Company ID`);
                    return;
                }
                
                // 🔥 Parsear fechas con logging detallado
                const startDate = parseExcelDate(row['Start Date (YYYY-MM-DD)']);
                const endDate = parseExcelDate(row['End Date (YYYY-MM-DD)']);
                
                console.log(`📅 Row ${index + 2} dates:`, {
                    raw_start: row['Start Date (YYYY-MM-DD)'],
                    raw_end: row['End Date (YYYY-MM-DD)'],
                    parsed_start: startDate,
                    parsed_end: endDate
                });
                
                // Validar que endDate sea posterior a startDate
                if (startDate && endDate) {
                    const start = new Date(startDate);
                    const end = new Date(endDate);
                    
                    if (end < start) {
                        errors.push(`Row ${index + 2}: End date (${endDate}) must be after start date (${startDate})`);
                        return;
                    }
                }
                
                const validRow = {
                    productCode: row['Product Code *'],
                    customPrice: customPrice,
                    companyId: row['Insurance Company ID *'],
                    minQuantity: parseInt(row['Min Quantity']) || 1,
                    maxQuantity: row['Max Quantity'] ? parseInt(row['Max Quantity']) : undefined,
                    priority: parseInt(row['Priority']) || 0,
                    currency: row['Currency'] || '€',
                    active: row['Active']?.toString().toLowerCase() === 'yes' || row['Active'] === true || row['Active'] === 'TRUE',
                    startDate: startDate,
                    endDate: endDate,
                    couponCodes: row['Coupon Codes (comma separated)'] 
                        ? row['Coupon Codes (comma separated)'].split(',').map(c => c.trim()).filter(c => c)
                        : []
                };
                
                console.log(`✅ Valid row ${index + 2}:`, validRow);
                validRows.push(validRow);
            });
            
            if (errors.length > 0) {
                console.error('❌ Validation errors:', errors);
                setErrorMessage(`❌ Validation errors:\n${errors.join('\n')}`);
                setLoading(false);
                return;
            }
            
            if (validRows.length === 0) {
                setErrorMessage('❌ No valid rows to import');
                setLoading(false);
                return;
            }
            
            console.log('✅ Valid rows to import:', validRows);
            
            // Crear pricelists
            const promises = validRows.map(row => {
                // Buscar productId por código
                const product = products.find(p => p.code === row.productCode);
                if (!product) {
                    throw new Error(`Product not found: ${row.productCode}`);
                }
                
                const dataToSend = {
                    productid: product._id,
                    companyid: row.companyId,
                    minQuantity: row.minQuantity,
                    maxQuantity: row.maxQuantity,
                    startDate: row.startDate, // Ya en formato YYYY-MM-DD
                    endDate: row.endDate,     // Ya en formato YYYY-MM-DD
                    price: row.customPrice,
                    currency: row.currency,
                    priority: row.priority,
                    active: row.active,
                    couponCodes: row.couponCodes,
                    conditions: {}
                };
                
                console.log('📤 Sending pricelist data:', dataToSend);
                return createPricelistCustomer(dataToSend);
            });
            
            await Promise.all(promises);
            
            setSuccessMessage(`✅ ${validRows.length} pricelists imported successfully!`);
            
            setTimeout(() => {
                onPricelistAdded();
                onClose();
                resetForm();
            }, 1500);
        };
        
        reader.readAsArrayBuffer(file);
        
    } catch (error) {
        console.error('❌ Import error:', error);
        setErrorMessage(error.message || 'Error importing file');
    } finally {
        setLoading(false);
    }
};

    const handleGlobalConfigChange = (e) => {
        const { name, value, checked, type } = e.target;
        setGlobalConfig(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleRowChange = (index, field, value) => {
        setProductRows(prev => {
            const newRows = [...prev];
            newRows[index] = {
                ...newRows[index],
                [field]: value
            };
            return newRows;
        });
    };

    const handleSelectAll = (checked) => {
        setProductRows(prev => prev.map(row => ({
            ...row,
            selected: checked
        })));
    };

    const handleDeleteRow = (index) => {
        setProductRows(prev => prev.filter((_, i) => i !== index));
    };

    const validateStep1 = () => {
        const selectedRows = productRows.filter(row => row.selected);
        
        if (selectedRows.length === 0) {
            setErrorMessage('Please select at least one product');
            return false;
        }

        for (let row of selectedRows) {
            if (!row.customPrice || parseFloat(row.customPrice) <= 0) {
                setErrorMessage(`Product "${row.productName}" must have a valid price`);
                return false;
            }
            if (!row.companyId) {
                setErrorMessage(`Product "${row.productName}" must have an insurance company selected`);
                return false;
            }
        }

        return true;
    };

    const validateStep2 = () => {
        if (globalConfig.minQuantity < 1) {
            setErrorMessage('Min quantity must be at least 1');
            return false;
        }

        if (globalConfig.maxQuantity && parseInt(globalConfig.maxQuantity) < parseInt(globalConfig.minQuantity)) {
            setErrorMessage('Max quantity must be greater than min quantity');
            return false;
        }

        if (globalConfig.startDate && globalConfig.endDate) {
            if (new Date(globalConfig.endDate) < new Date(globalConfig.startDate)) {
                setErrorMessage('End date must be after start date');
                return false;
            }
        }

        return true;
    };

    const handleNext = () => {
        setErrorMessage('');
        if (activeStep === 0) {
            if (validateStep1()) {
                setActiveStep(1);
            }
        }
    };

    const handleBack = () => {
        setActiveStep(0);
        setErrorMessage('');
    };

    const handleSubmit = async () => {
        if (!validateStep2()) {
            return;
        }

        setLoading(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const selectedRows = productRows.filter(row => row.selected);
            
            const promises = selectedRows.map(row => {
                const dataToSend = {
                    productid: row.productId,
                    companyid: row.companyId,
                    userid: globalConfig.userid || undefined,
                    minQuantity: parseInt(globalConfig.minQuantity),
                    maxQuantity: globalConfig.maxQuantity ? parseInt(globalConfig.maxQuantity) : undefined,
                    startDate: globalConfig.startDate || undefined,
                    endDate: globalConfig.endDate || undefined,
                    price: parseFloat(row.customPrice),
                    currency: globalConfig.currency,
                    priority: parseInt(globalConfig.priority),
                    active: globalConfig.active,
                    couponCodes: globalConfig.couponCodes ? globalConfig.couponCodes.split(',').map(c => c.trim()) : [],
                    conditions: {}
                };

                console.log('📤 Sending pricelist data:', dataToSend);
                return createPricelistCustomer(dataToSend);
            });

            await Promise.all(promises);
            
            setSuccessMessage(
                `✅ ${selectedRows.length} pricelist${selectedRows.length > 1 ? 's' : ''} created successfully!`
            );

            setTimeout(() => {
                onPricelistAdded();
                onClose();
                resetForm();
            }, 1500);
        } catch (error) {
            console.error('❌ Error saving pricelists:', error);
            setErrorMessage(error.response?.data?.message || 'Error saving pricelists');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setProductRows([]);
        setGlobalConfig({
            userid: '',
            minQuantity: 1,
            maxQuantity: '',
            startDate: '',
            endDate: '',
            currency: '€',
            priority: 0,
            active: true,
            couponCodes: ''
        });
        setActiveStep(0);
        setSuccessMessage('');
        setErrorMessage('');
    };

    const selectedCount = productRows.filter(row => row.selected).length;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: { xs: '100%', sm: '90%', md: 1100 } }
            }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* HEADER */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Typography variant="h5" gutterBottom>
                        📊 Bulk Customer Pricing (Excel Style)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Step {activeStep + 1}: {activeStep === 0 ? 'Select products and set prices' : 'Configure global settings'}
                    </Typography>
                    
                    <Stepper activeStep={activeStep} sx={{ mt: 2 }}>
                        <Step>
                            <StepLabel>Product Selection & Pricing</StepLabel>
                        </Step>
                        <Step>
                            <StepLabel>Global Configuration</StepLabel>
                        </Step>
                    </Stepper>
                </Box>

                <Divider />

                {successMessage && (
                    <Alert severity="success" sx={{ m: 2 }}>
                        {successMessage}
                    </Alert>
                )}

                {errorMessage && (
                    <Alert severity="error" sx={{ m: 2 }}>
                        {errorMessage}
                    </Alert>
                )}

                {/* CONTADOR */}
                <Box sx={{ mx: 3, mt: 2, p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: 1 }}>
                    <Typography variant="subtitle2">
                        {selectedCount} product(s) selected | Ready to create {selectedCount} pricelist(s)
                    </Typography>
                </Box>

                {/* 🔥 BOTONES DE EXPORT/IMPORT EXCEL */}
                {activeStep === 0 && (
                    <Box sx={{ mx: 3, mt: 2, display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={exportBlankTemplate}
                            disabled={selectedCount === 0 || loadingCompanies}
                        >
                            📥 Download Template ({selectedCount} products)
                        </Button>
                        
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<UploadIcon />}
                            disabled={loading}
                        >
                            📤 Import from Excel
                            <input
                                type="file"
                                hidden
                                accept=".xlsx,.xls"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) importFromExcel(file);
                                    e.target.value = ''; // Reset
                                }}
                            />
                        </Button>
                    </Box>
                )}

                {/* LOADING COMPANIES */}
                {loadingCompanies && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Loading insurance companies...</Typography>
                    </Box>
                )}

                {/* CONTENIDO PRINCIPAL */}
                <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    {activeStep === 0 && (
                        // STEP 1: TABLA DE PRODUCTOS
                        <TableContainer component={Paper} sx={{ m: 3, flex: 1, overflow: 'auto' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                indeterminate={selectedCount > 0 && selectedCount < productRows.length}
                                                checked={productRows.length > 0 && selectedCount === productRows.length}
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                            />
                                        </TableCell>
                                        <TableCell><strong>Code</strong></TableCell>
                                        <TableCell><strong>Product Name</strong></TableCell>
                                        <TableCell><strong>Original Price</strong></TableCell>
                                        <TableCell><strong>Custom Price *</strong></TableCell>
                                        <TableCell sx={{ minWidth: 200 }}><strong>Insurance Company *</strong></TableCell>
                                        <TableCell><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {productRows.map((row, index) => (
                                        <TableRow 
                                            key={row.productId}
                                            sx={{ 
                                                bgcolor: row.selected ? 'action.selected' : 'inherit',
                                                '&:hover': { bgcolor: 'action.hover' }
                                            }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={row.selected}
                                                    onChange={(e) => handleRowChange(index, 'selected', e.target.checked)}
                                                />
                                            </TableCell>
                                            <TableCell>{row.productCode}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ maxWidth: 250 }}>
                                                    {row.productName}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>${row.originalPrice}</TableCell>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={row.customPrice}
                                                    onChange={(e) => handleRowChange(index, 'customPrice', e.target.value)}
                                                    placeholder="0.00"
                                                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                                    sx={{ width: 120 }}
                                                    disabled={!row.selected}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <FormControl size="small" fullWidth>
                                                    <Select
                                                        value={row.companyId}
                                                        onChange={(e) => handleRowChange(index, 'companyId', e.target.value)}
                                                        displayEmpty
                                                        disabled={!row.selected || loadingCompanies}
                                                        MenuProps={{
                                                            PaperProps: {
                                                                style: {
                                                                    maxHeight: 300,
                                                                    overflow: 'auto'
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <MenuItem value="">
                                                            <em>
                                                                {loadingCompanies ? 'Loading...' : 'Select insurance...'}
                                                            </em>
                                                        </MenuItem>
                                                        {insuranceCompanies.map(company => (
                                                            <MenuItem key={company._id} value={company._id}>
                                                                {company.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell>
                                                <IconButton 
                                                    size="small" 
                                                    color="error"
                                                    onClick={() => handleDeleteRow(index)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {activeStep === 1 && (
                        // STEP 2: CONFIGURACIÓN GLOBAL
                        <Box sx={{ p: 3, overflow: 'auto' }}>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                These settings will apply to all {selectedCount} selected products
                            </Alert>

                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    ⚙️ Global Configuration
                                </Typography>
                                
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3, mt: 2 }}>
                                    <TextField
                                        label="User ID (optional)"
                                        name="userid"
                                        value={globalConfig.userid}
                                        onChange={handleGlobalConfigChange}
                                        helperText="ObjectId del usuario específico"
                                        fullWidth
                                    />

                                    <FormControl fullWidth>
                                        <InputLabel>Currency</InputLabel>
                                        <Select
                                            name="currency"
                                            value={globalConfig.currency}
                                            onChange={handleGlobalConfigChange}
                                            label="Currency"
                                        >
                                            <MenuItem value="€">€ EUR</MenuItem>
                                            <MenuItem value="$">$ USD</MenuItem>
                                            <MenuItem value="£">£ GBP</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        type="number"
                                        label="Min Quantity *"
                                        name="minQuantity"
                                        value={globalConfig.minQuantity}
                                        onChange={handleGlobalConfigChange}
                                        InputProps={{ inputProps: { min: 1 } }}
                                        fullWidth
                                    />

                                    <TextField
                                        type="number"
                                        label="Max Quantity (optional)"
                                        name="maxQuantity"
                                        value={globalConfig.maxQuantity}
                                        onChange={handleGlobalConfigChange}
                                        InputProps={{ inputProps: { min: 1 } }}
                                        fullWidth
                                    />

                                    <TextField
                                        type="date"
                                        label="Start Date"
                                        name="startDate"
                                        value={globalConfig.startDate}
                                        onChange={handleGlobalConfigChange}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                    />

                                    <TextField
                                        type="date"
                                        label="End Date"
                                        name="endDate"
                                        value={globalConfig.endDate}
                                        onChange={handleGlobalConfigChange}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                    />

                                    <TextField
                                        type="number"
                                        label="Priority"
                                        name="priority"
                                        value={globalConfig.priority}
                                        onChange={handleGlobalConfigChange}
                                        helperText="Higher = more priority"
                                        InputProps={{ inputProps: { min: 0 } }}
                                        fullWidth
                                    />

                                    <TextField
                                        label="Coupon Codes"
                                        name="couponCodes"
                                        value={globalConfig.couponCodes}
                                        onChange={handleGlobalConfigChange}
                                        placeholder="CODE1, CODE2"
                                        helperText="Comma separated"
                                        fullWidth
                                    />
                                </Box>

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={globalConfig.active}
                                            onChange={handleGlobalConfigChange}
                                            name="active"
                                            color="primary"
                                        />
                                    }
                                    label="Active"
                                    sx={{ mt: 2 }}
                                />
                            </Paper>
                        </Box>
                    )}
                </Box>

                {/* BOTONES DE NAVEGACIÓN */}
                <Divider />
                <Box sx={{ p: 3, display: 'flex', gap: 2 }}>
                    {activeStep === 0 ? (
                        <>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => {
                                    onClose();
                                    resetForm();
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleNext}
                                disabled={selectedCount === 0 || loadingCompanies}
                            >
                                Next: Configure Settings →
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={handleBack}
                                disabled={loading}
                            >
                                ← Back
                            </Button>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? 'Creating...' : `Create ${selectedCount} Pricelist(s)`}
                            </Button>
                        </>
                    )}
                </Box>
            </Box>
        </Drawer>
    );
};

export default AddCustomerPriceDrawer;